import React from 'react';

import { Typography } from '@mui/material';
import _ from 'lodash';

import { currencyOptions } from 'constants/loanFee';
import { getPendingChangesActionType } from 'utils/loanFees';
import { formatNumberToCurrencyString } from 'utils/number';
import {
  getFieldPendingChangeAuthors,
  mergeFieldPendingChangeAuthors,
} from 'utils/pendingChangesAuthors';

import { FEE_NAME_FIELD, FEE_NAME_HISTORY_FIELDS } from './FeeTable.constants';

export const checkIsTotalPayerAmountPresent = ({ fee, field, isTotalView }) =>
  isTotalView && !_.isNil(fee[field]);

export const getPayerCellPendingChangesActionType = ({ fee, field, isTotalView }) =>
  isTotalView && !checkIsTotalPayerAmountPresent({ fee, field, isTotalView })
    ? null
    : getPendingChangesActionType(fee.modifiedFields, field);

export const getCombinedAppraisalFeeAmount = (baseAmount, appraisalSurcharges, field) =>
  Number(baseAmount || 0) +
  _.sumBy(appraisalSurcharges, surcharge => Number(surcharge[field] || 0));

export const getAppraisalSurchargeTooltipContent = appraisalSurcharges => (
  <>
    {appraisalSurcharges.map(({ feeTypeId, feeName, feeTypeName, amount }, index) => (
      <Typography key={feeTypeId ?? index} variant="regular14">
        {feeTypeName ? `${feeName} (${feeTypeName})` : feeName}{' '}
        {`+${formatNumberToCurrencyString(amount || 0, currencyOptions)}`}
      </Typography>
    ))}
  </>
);

const checkIsFeeEntityKey = (entityKey, { feeTypeId, applicationImpoundId }) =>
  (!!feeTypeId && entityKey.includes(feeTypeId)) || entityKey === applicationImpoundId;

/**
 * A fee row's pending-change authors by field. A loan fee's history key combines the loan with the fee
 * type id, so it is matched on that id; an impound-backed row (prepaids, escrows) has its own entity.
 */
export const getFeeFieldPendingChangeAuthors = (fieldAuthorsByEntityKey, fee) => {
  if (_.isEmpty(fieldAuthorsByEntityKey) || !fee) {
    return {};
  }

  const fieldAuthors = mergeFieldPendingChangeAuthors(
    Object.keys(fieldAuthorsByEntityKey)
      .filter(entityKey => checkIsFeeEntityKey(entityKey, fee))
      .map(entityKey => fieldAuthorsByEntityKey[entityKey]),
  );
  const nameAuthors = _.uniqBy(
    FEE_NAME_HISTORY_FIELDS.flatMap(
      fieldName => getFieldPendingChangeAuthors(fieldAuthors, fieldName) ?? [],
    ),
    'key',
  );

  return _.isEmpty(nameAuthors) ? fieldAuthors : { ...fieldAuthors, [FEE_NAME_FIELD]: nameAuthors };
};
